import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from "./axios";
import { showError, showSuccess } from "../actions/globalActions";

// Import shared types
import type { BasketballStat } from '@staty/shared/schemas/stat';

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token: string) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

// Type for the stat store state and actions
interface StatState {
  stats: BasketballStat[];
  setStats: (stats: BasketballStat[]) => void;
  getStatsByPlayerId: (id: string) => Promise<void>;
  getStatsByTeamId: (id: string) => Promise<void>;
  getStatsByGameId: (id: string) => Promise<void>;
  updateStat: (getToken: () => Promise<string>) => (stat: BasketballStat, cb?: (id: string) => void) => Promise<void>;
  addStat: (getToken: () => Promise<string>) => (stat: Partial<BasketballStat>, cb?: (id: string) => void) => Promise<void>;
  bulkUpsertStats: (getToken: () => Promise<string>) => (rows: Partial<BasketballStat>[], cb?: () => void) => Promise<any[]>;
}

const useStatStore = create<StatState>()(
  immer((set) => ({
    stats: [],

    // Methods to manipulate the state
    setStats: (stats: BasketballStat[]) => {
      set((state) => {
        state.stats = stats;
      });
    },

    // Asynchronous actions
    getStatsByPlayerId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/stats/player/${id}?include=vsTeam,game,organization(name,_id),league(name,_id),player(name,_id)`);
        set((state) => {
          state.stats = data.result;
        });
      } catch (error) {
        console.error("Error in getStatsByPlayerId:", error);
      }
    },

    getStatsByTeamId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/stats/team/${id}?include=team,vsTeam,game,player,organization,league`);
        set((state) => {
          state.stats = data.result;
        });
      } catch (error) {
        console.error("Error in getStatsByTeamId:", error);
      }
    },

    getStatsByGameId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/stats/game/${id}?include=player,game`);
        set((state) => {
          state.stats = data.result;
        });
      } catch (error) {
        console.error("Error in getStatsByGameId:", error);
      }
    },

    updateStat: (getToken: () => Promise<string>) => {
      return async (stat: BasketballStat, cb?: (id: string) => void) => {
        try {
          const token = await getToken();
          const { data } = await axios.put(`${baseUrl}/stats/${stat._id}`, stat, createAuthHeader(token));
          showSuccess('Success', 'Successfully Updated');
          set((state) => {
            const index = state.stats.findIndex((s) => s._id === stat._id);
            if (index !== -1) {
              state.stats[index] = data.result;
            }
          });
          if (cb && stat._id) cb(stat._id);
        } catch (error) {
          showError('Error', 'Update Error');
          console.error("Error in updateStat:", error);
        }
      };
    },

    addStat: (getToken: () => Promise<string>) => {
      return async (stat: Partial<BasketballStat>, cb?: (id: string) => void) => {
        try {
          const token = await getToken();
          const { data } = await axios.post(`${baseUrl}/stats/d`, stat, createAuthHeader(token));
          showSuccess('Success', 'Successfully Added');
          set((state) => {
            state.stats.push(data.result);
          });
          if (cb && stat._id) cb(stat._id);
        } catch (error) {
          showError('Error', 'Add Error');
          console.error("Error in addStat:", error);
        }
      };
    },

    // Bulk create/update many stats in one request (upsert on player+game).
    bulkUpsertStats: (getToken: () => Promise<string>) => {
      return async (rows: Partial<BasketballStat>[], cb?: () => void) => {
        try {
          const token = await getToken();
          const { data } = await axios.post(`${baseUrl}/stats/bulk`, rows, createAuthHeader(token));
          const saved = data.result || [];
          set((state) => {
            saved.forEach((doc: BasketballStat) => {
              const idx = state.stats.findIndex(
                (s) => String((s as any).player) === String((doc as any).player) && String(s.game) === String(doc.game)
              );
              if (idx !== -1) state.stats[idx] = doc;
              else state.stats.push(doc);
            });
          });
          if (cb) cb();
          return saved;
        } catch (error) {
          showError('Error', 'Save Error');
          console.error("Error in bulkUpsertStats:", error);
          throw error;
        }
      };
    },
  }))
);

export default useStatStore;
